import { FunctionComponent, useEffect, useState } from 'react'
import { Daycare, DaycareType } from '@/types/Daycare'
import { Section, SectionGroup } from '@/types/Section'
import SectionsRow from './sectionsTableRow'
import styles from './sections.module.scss'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import Title from '@/components/_custom/Title/title'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import Desktop from '@/modules/desktopMobile/desktop'
import SectionDialog from './Dialog/sectionDialog'
import { useAuth } from '@/modules/context/AuthContext'
import { useQuery } from '@apollo/client'
import { GET_SECTIONS } from '@/modules/API/daycare/sectionHandler'
import Loading from '@/components/Loading/Loading'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'

interface SectionsProps {
    daycare: Daycare
    canUpdate: boolean
}

const Sections: FunctionComponent<SectionsProps> = ({ daycare, canUpdate }: SectionsProps) => {
    const { t } = useTranslation()

    const { user, fetchCurrentDaycare } = useAuth()

    const [sectionsList, setSectionsList] = useState<Array<Section>>([])
    const [sectionGroupList, setSectionGroupList] = useState<Array<SectionGroup>>([])

    const [isSectionDialogOpen, setSectionDialogOpen] = useState<boolean>(false)

    const {
        loading: loadingSections,
        data: dataSections,
        error: errorSections,
        refetch: refetchSectionsFromQuery
    } = useQuery<{ getSections: Section[] }>(GET_SECTIONS, {
        fetchPolicy: 'network-only',
        variables: {
            daycare_id: daycare.id
        }
    })

    useEffect(() => {
        if (dataSections) {
            setSectionsList(
                [...dataSections.getSections].sort((a: Section, b: Section) => a.name.localeCompare(b.name))
            )
        }
    }, [dataSections])

    useEffect(() => {
        setSectionGroupList([])

        if (user?.currentDaycare?.sectionsGroups && user?.currentDaycare?.sectionsGroups.length > 0) {
            setSectionGroupList(user?.currentDaycare?.sectionsGroups)
        }
    }, [user?.currentDaycare?.sectionsGroups])

    function refetchSections(): void {
        refetchSectionsFromQuery()
        fetchCurrentDaycare()
    }

    return (
        <div className={styles.SectionPlacement}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <Title
                        text={`${
                            daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep
                                ? t('mySectors')
                                : t('mySections')
                        }`}
                        className={styles.Title}
                    />
                    {canUpdate && (
                        <ButtonTextIcon
                            type={'add'}
                            onClick={() => setSectionDialogOpen(true)}
                            disabled={isSectionDialogOpen}
                            className={styles.Button}
                        />
                    )}
                </div>
                {loadingSections && <Loading overall={false} />}
                {dataSections && (
                    <>
                        <TableContainer className={styles.TablePlacement}>
                            <Table className={styles.Table}>
                                <TableHead className={styles.TableHeader}>
                                    <TableRow>
                                        <TableCell sx={{ width: '40%' }}>{t('name')}</TableCell>
                                        <Desktop>
                                            <TableCell sx={{ width: '20%' }}>{t('seatsNumber')}</TableCell>
                                            {daycare.staffAccess && (
                                                <TableCell sx={{ width: '30%' }}>{t('management')}</TableCell>
                                            )}
                                        </Desktop>
                                        {canUpdate && (
                                            <TableCell colSpan={2} sx={{ width: '10%' }}>
                                                {t('actions')}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sectionsList.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4}>{t('noSections')}</TableCell>
                                        </TableRow>
                                    ) : (
                                        sectionsList.map((section: Section, index: number) => (
                                            <SectionsRow
                                                key={
                                                    section.id +
                                                    section.name +
                                                    section.sectionGroupId +
                                                    section.max +
                                                    index
                                                }
                                                section={section}
                                                daycare={daycare}
                                                canUpdate={canUpdate}
                                                sectionGroups={sectionGroupList}
                                                refetchSections={refetchSections}
                                            />
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <SectionDialog
                            open={isSectionDialogOpen}
                            handleClose={() => setSectionDialogOpen(false)}
                            daycare={daycare}
                            create={true}
                            sectionGroups={sectionGroupList}
                            refetchSections={refetchSections}
                        />
                    </>
                )}
                {errorSections && <ErrorMessage error={t('generalError')} />}
            </div>
        </div>
    )
}

export default Sections
